import { businessEntityKeys } from '@commenda/api/business-entity/keys';
import { IncorporationRequestDataType } from '@commenda/types';
import { Dialog, DialogContent } from '@commenda/kakashi';
import { CorporationDataType } from '@commenda/types/src/corporation';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getCorporationName } from '@commenda/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { DirectorFormType } from '../../Director/DirectorFormType';
import AuthorizedSignatoryDetails from './AuthorizedSignatoryDetails';
import CorporationDetailsForm from './CorporationDetailsForm';
import {
  AuthorizedSignatoryMode,
  CorporateStakeholderModalState,
} from './types';

const CorporateStakeholderModal = ({
  isShown,
  toggle,
  postSave,
  companyId,
  mutate,
  setNotification,
  setSelectedCorporateStakeholderId,
  selectedCorporateStakeholderId,
  // Overrides the header text in cases where corporate shareholder shouldn't be referred to as a 'corporate shareholder'.
  // Ex: in the UK, they're referred to as corporate members.
  headerTextOverride,
  DirectorForm,
  directors,
  mutateDirectors,
  corporations,
  workRequestData,
}: {
  isShown: boolean;
  toggle: () => void;
  postSave: ({
    corporationId,
    corporationName,
  }: {
    corporationId: number;
    corporationName: string;
  }) => Promise<void>;
  companyId: number;
  setSelectedCorporateStakeholderId: React.Dispatch<
    React.SetStateAction<number>
  >;
  mutate: () => Promise<void>;
  setNotification: TSetNotification;
  selectedCorporateStakeholderId?: number;
  headerTextOverride?: string;
  DirectorForm: DirectorFormType<DirectorBase>;
  directors: DirectorBase[];
  mutateDirectors: KeyedMutator<unknown>;
  corporations: CorporationDataType[];
  workRequestData?: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
}) => {
  const queryClient = useQueryClient();

  const directorWithAuthorizedSignatory = useMemo(() => {
    return directors.find(
      (director) =>
        director.corporationHasDirector?.find(
          (chd) => chd.corporationId === selectedCorporateStakeholderId
        )?.additionalDirectorInformation?.isAuthorizedSignatory
    );
  }, [directors, selectedCorporateStakeholderId]);

  const [selectedAuthorizedSignatory, setSelectedAuthorizedSignatory] =
    useState<DirectorBase | null>(null);

  const [step, setStep] = useState<CorporateStakeholderModalState>(
    'CORPORATION_DETAILS'
  );

  const onClose = () => {
    setStep('CORPORATION_DETAILS');
    setSelectedAuthorizedSignatory(null);
    toggle();
  };

  let jsx;
  switch (step) {
    case 'CORPORATION_DETAILS':
      jsx = (
        <CorporationDetailsForm
          onClose={toggle}
          onNext={async (corporation: { id: number }) => {
            queryClient.invalidateQueries({
              queryKey: [
                businessEntityKeys.FETCH_BUSINESS_ENTITY,
                corporation.id,
              ],
            });
            await mutate();
            setSelectedCorporateStakeholderId(corporation.id);
            if (directorWithAuthorizedSignatory) {
              setStep('AUTHORISED_SIGNATORY_DETAILS');
            } else {
              setStep('CHOOSE_NEW_OR_EXISTING_AUTHORISED_SIGNATORY');
            }
          }}
          corporateStakeholderId={selectedCorporateStakeholderId}
          companyId={companyId}
          setNotification={setNotification}
        />
      );
      break;
    case 'CHOOSE_NEW_OR_EXISTING_AUTHORISED_SIGNATORY':
      jsx = (
        <AuthorizedSignatoryDetails
          onBack={() => setStep('CORPORATION_DETAILS')}
          onNext={() => setStep('AUTHORISED_SIGNATORY_DETAILS')}
          companyId={companyId}
          existingAuthorizedSignatory={selectedAuthorizedSignatory}
          onModeChange={(mode: AuthorizedSignatoryMode) => {
            if (mode === 'new') {
              setSelectedAuthorizedSignatory(null);
            } else {
              setSelectedAuthorizedSignatory(
                directorWithAuthorizedSignatory || null
              );
            }
          }}
          onSelectDirector={(directorId: number) => {
            const selectedDirector = directors.find(
              (director) => director.id === directorId
            );
            if (selectedDirector) {
              setSelectedAuthorizedSignatory(selectedDirector);
            }
          }}
          directors={directors}
        />
      );
      break;
    case 'AUTHORISED_SIGNATORY_DETAILS':
      jsx = (
        <DirectorForm
          director={selectedAuthorizedSignatory}
          companyDataMutate={mutateDirectors}
          isAddingDirector={false}
          setIsAddingDirector={() => {}}
          setOpen={() => {
            if (directorWithAuthorizedSignatory) {
              setStep('CORPORATION_DETAILS');
            } else {
              setStep('CHOOSE_NEW_OR_EXISTING_AUTHORISED_SIGNATORY');
            }
          }}
          corporationId={selectedCorporateStakeholderId}
          setNotification={setNotification}
          companyId={companyId}
          token={undefined}
          modalConfig={{
            heading: 'Authorised Signatory Details',
            isAuthorizedSignatory: true,
            postSave: () => {
              const selectedCorporateStakeholder = corporations.find(
                (corporation) =>
                  corporation.id === selectedCorporateStakeholderId
              );
              if (!selectedCorporateStakeholder) return;

              const corporationName = getCorporationName(
                selectedCorporateStakeholder
              );

              postSave({
                corporationId: selectedCorporateStakeholderId,
                corporationName,
              });

              onClose();
            },
          }}
          workRequestData={workRequestData}
        />
      );
      break;
    default:
      jsx = null;
      break;
  }

  // If the corporate shareholder has an authorized signatory, set the existing authorized signatory id
  useEffect(() => {
    if (directorWithAuthorizedSignatory)
      setSelectedAuthorizedSignatory(directorWithAuthorizedSignatory);
  }, [directorWithAuthorizedSignatory, selectedCorporateStakeholderId]);

  useEffect(() => {
    if (isShown) {
      setStep('CORPORATION_DETAILS');
    }
  }, [isShown]);

  return (
    <Dialog open={isShown} onOpenChange={onClose}>
      <DialogContent
        className="overflow-hidden max-h-[85vh] flex flex-col"
        preventCloseOnOutsideClick
      >
        <div className="flex flex-col grow">{jsx}</div>
      </DialogContent>
    </Dialog>
  );
};

export default CorporateStakeholderModal;
